/**
 * Sale frequency per FIN (FRIDGE-792) — "Below its 60-day median on N of the
 * last M days tracked · typical dip P% · Lowest tracked $X on date".
 *
 * Built on the SAME series and the SAME density floor as the deal guard
 * (lib/deals/derive.ts, FRIDGE-761): a series too thin to support a deal is
 * too thin to support a frequency claim, so under the floor this returns null
 * and the page renders nothing (Real-or-Hidden).
 *
 * DAY-COUNTING. Readings arrive every ~14 days (the refresh rotation reads
 * ~1,000 listings a day across ~14,500), so "days below the median" is
 * counted on the CARRIED-FORWARD price — a reading holds until the next one —
 * from the first in-window reading through `asOf`. The denominator is that
 * tracked span, printed as measured, never rounded up to "60".
 *
 * PURE: no I/O, no Date.now() inside the math — the caller passes `asOf`.
 */
import { DEALS_CONFIG, isoDateMs, median, seriesDensity, type SnapshotRow } from './derive'

const MS_PER_DAY = 24 * 60 * 60 * 1000

export interface SaleFrequency {
  /** The window the median and the day counts are measured over. */
  windowDays: number
  /** Median of the window's readings — "recent typical". */
  median60Cents: number
  /** Days (carried-forward price) strictly below the median, inside the tracked span. */
  daysBelowMedian: number
  /** Days from the first in-window reading through asOf, inclusive (≤ windowDays + 1). */
  daysTracked: number
  /** Lowest reading in the FULL series held for this product, and its date. */
  lowestCents: number
  lowestDate: string
  /** Median depth of the window's below-median readings as a fraction of the median; null when none. */
  typicalDipFraction: number | null
  /** Valid readings inside the window, distinct days, and ten-day buckets with a reading. */
  readings: number
  distinctDays: number
  spreadBuckets: number
}

function floatMedian(values: number[]): number {
  const s = [...values].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 === 0 ? (s[mid - 1] + s[mid]) / 2 : s[mid]
}

export function computeSaleFrequency(rows: ReadonlyArray<SnapshotRow>, asOf: Date = new Date()): SaleFrequency | null {
  const valid: Array<{ date: string; cents: number }> = []
  for (const r of rows) {
    if (typeof r.new_price_cents !== 'number' || !(r.new_price_cents > 0)) continue
    if (typeof r.snapshot_date !== 'string') continue
    const date = r.snapshot_date.slice(0, 10)
    if (Number.isNaN(isoDateMs(date))) continue
    valid.push({ date, cents: r.new_price_cents })
  }
  if (valid.length === 0) return null
  valid.sort((a, b) => a.date.localeCompare(b.date))

  const nowMs = asOf.getTime()
  const windowDays = DEALS_CONFIG.medianWindowDays
  const cutMs = nowMs - windowDays * MS_PER_DAY
  const inWindow = valid.filter((r) => isoDateMs(r.date) >= cutMs)
  if (inWindow.length < DEALS_CONFIG.minSnapshots) return null

  const { distinctDays, spreadBuckets } = seriesDensity(inWindow, nowMs)
  if (distinctDays < DEALS_CONFIG.minSnapshotDays) return null
  if (spreadBuckets < DEALS_CONFIG.minSpreadBuckets) return null

  const median60 = median(inWindow.map((r) => r.cents))
  if (median60 === null || median60 <= 0) return null

  // One price per day (the lowest, as the page's collapse does), then carry forward.
  const perDay = new Map<string, number>()
  for (const r of inWindow) {
    const seen = perDay.get(r.date)
    if (seen === undefined || r.cents < seen) perDay.set(r.date, r.cents)
  }
  const firstDay = [...perDay.keys()].sort()[0]
  const todayMs = Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth(), asOf.getUTCDate())
  let daysTracked = 0
  let daysBelowMedian = 0
  let price = perDay.get(firstDay)!
  for (let t = isoDateMs(firstDay); t <= todayMs; t += MS_PER_DAY) {
    const day = new Date(t).toISOString().slice(0, 10)
    const reading = perDay.get(day)
    if (reading !== undefined) price = reading
    daysTracked += 1
    if (price < median60) daysBelowMedian += 1
  }

  // Lowest over the FULL series; the earliest date wins a tie.
  let lowest = valid[0]
  for (const r of valid) if (r.cents < lowest.cents) lowest = r

  const dips = inWindow.filter((r) => r.cents < median60).map((r) => 1 - r.cents / median60)
  const typicalDipFraction = dips.length > 0 ? floatMedian(dips) : null

  return {
    windowDays,
    median60Cents: median60,
    daysBelowMedian,
    daysTracked,
    lowestCents: lowest.cents,
    lowestDate: lowest.date,
    typicalDipFraction,
    readings: inWindow.length,
    distinctDays,
    spreadBuckets,
  }
}

/** Whole dollars when the cents are zero, otherwise two decimals — one text token. */
function dollars(cents: number): string {
  const whole = cents % 100 === 0
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 })}`
}

/** "YYYY-MM-DD" → "MM/DD/YYYY" (body-text date rule). */
function usDate(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${m}/${d}/${y}`
}

/** The one honest line. Every figure is a real reading; nothing is rounded up. */
export function formatSaleFrequencyLine(sf: SaleFrequency): string {
  const dip = sf.typicalDipFraction !== null ? ` · typical dip ${Math.round(sf.typicalDipFraction * 100)}%` : ''
  return (
    `Below its ${sf.windowDays}-day median on ${sf.daysBelowMedian} of the last ${sf.daysTracked} days tracked` +
    `${dip} · Lowest tracked ${dollars(sf.lowestCents)} on ${usDate(sf.lowestDate)}`
  )
}
